export const CERT_ALLOWANCE_CERTIFICATION_TYPES = [
  'AHPRA_REGISTRATION',
  'NDIS_WORKER_SCREENING',
  'POLICE_CHECK',
  'FIRST_AID',
  'CPR',
  'MANDATORY_TRAINING',
  'QUALIFICATION',
  'IMMUNISATION',
  'DRIVER_LICENCE',
  'OTHER',
] as const;

export type CertAllowanceCertificationType = (typeof CERT_ALLOWANCE_CERTIFICATION_TYPES)[number];

export type CertGateStatus = 'ACTIVE' | 'ARCHIVED';

export interface CertAllowanceRequirementInput {
  id?: string | null;
  tenantId?: string | null;
  tenant_id?: string | null;
  awardCode?: string | null;
  award_code?: string | null;
  allowanceCode?: string | null;
  allowance_code?: string | null;
  certificationType?: string | null;
  certification_type?: string | null;
  requirementCode?: string | null;
  requirement_code?: string | null;
  requirementLabel?: string | null;
  requirement_label?: string | null;
  required?: boolean | null;
  status?: string | null;
  effectiveFrom?: string | null;
  effective_from?: string | null;
  effectiveTo?: string | null;
  effective_to?: string | null;
  updatedAt?: string | null;
  updated_at?: string | null;
  createdAt?: string | null;
  created_at?: string | null;
}

export interface CertGateRequirement {
  id: string | null;
  tenantId: string | null;
  awardCode: string;
  allowanceCode: string;
  certificationType: CertAllowanceCertificationType;
  requirementCode: string;
  requirementLabel: string;
  required: boolean;
  status: CertGateStatus;
  effectiveFrom: string;
  effectiveTo: string | null;
  updatedAt: string | null;
  createdAt: string | null;
}

export interface ActiveCertificationInput {
  tenantId?: string | null;
  tenant_id?: string | null;
  staffId?: string | null;
  staff_id?: string | null;
  certificationType?: string | null;
  certification_type?: string | null;
  requirementCode?: string | null;
  requirement_code?: string | null;
  status?: string | null;
  verificationStatus?: string | null;
  verification_status?: string | null;
  expiresAt?: string | null;
  expires_at?: string | null;
}

export type CertGateRegistry =
  | ReadonlyMap<string, CertGateRequirement>
  | Readonly<Record<string, CertGateRequirement>>;

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

function firstString(...values: Array<string | null | undefined>): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  }
  return null;
}

function upperToken(value: string | null | undefined): string | null {
  const text = firstString(value);
  return text ? text.toUpperCase() : null;
}

function dateOnly(value: string | null | undefined, fieldName: string): string | null {
  const text = firstString(value);
  if (!text) return null;
  const candidate = text.slice(0, 10);
  if (!DATE_ONLY_RE.test(candidate)) {
    throw new Error(`${fieldName} must be a YYYY-MM-DD date`);
  }
  return candidate;
}

function requiredDateOnly(value: string | null | undefined, fieldName: string): string {
  const parsed = dateOnly(value, fieldName);
  if (!parsed) throw new Error(`${fieldName} is required`);
  return parsed;
}

export function certAllowanceKey(awardCode: string, allowanceCode: string): string {
  return `${awardCode.trim().toUpperCase()}:${allowanceCode.trim().toUpperCase()}`;
}

export function isCertAllowanceCertificationType(
  value: string | null | undefined,
): value is CertAllowanceCertificationType {
  const token = upperToken(value);
  return Boolean(
    token && (CERT_ALLOWANCE_CERTIFICATION_TYPES as readonly string[]).includes(token),
  );
}

export function normaliseCertAllowanceRequirement(
  input: CertAllowanceRequirementInput,
): CertGateRequirement {
  const awardCode = upperToken(input.awardCode ?? input.award_code);
  const allowanceCode = upperToken(input.allowanceCode ?? input.allowance_code);
  const certificationType = upperToken(input.certificationType ?? input.certification_type);
  const requirementCode = upperToken(input.requirementCode ?? input.requirement_code);
  const requirementLabel = firstString(input.requirementLabel ?? input.requirement_label);
  const status = upperToken(input.status) ?? 'ACTIVE';

  if (!awardCode) throw new Error('awardCode is required');
  if (!allowanceCode) throw new Error('allowanceCode is required');
  if (!isCertAllowanceCertificationType(certificationType)) {
    throw new Error(`certificationType is not supported: ${certificationType ?? ''}`);
  }
  if (!requirementCode) throw new Error('requirementCode is required');
  if (!requirementLabel) throw new Error('requirementLabel is required');
  if (status !== 'ACTIVE' && status !== 'ARCHIVED') {
    throw new Error(`status is not supported: ${status}`);
  }

  return {
    id: input.id ?? null,
    tenantId: input.tenantId ?? input.tenant_id ?? null,
    awardCode,
    allowanceCode,
    certificationType,
    requirementCode,
    requirementLabel,
    required: input.required ?? true,
    status,
    effectiveFrom: requiredDateOnly(input.effectiveFrom ?? input.effective_from, 'effectiveFrom'),
    effectiveTo: dateOnly(input.effectiveTo ?? input.effective_to, 'effectiveTo'),
    updatedAt: firstString(input.updatedAt ?? input.updated_at),
    createdAt: firstString(input.createdAt ?? input.created_at),
  };
}

export function isCertGateRequirementEffective(
  requirement: CertGateRequirement,
  asOfDate: string,
): boolean {
  const asOf = requiredDateOnly(asOfDate, 'asOfDate');
  return (
    requirement.status === 'ACTIVE' &&
    requirement.effectiveFrom <= asOf &&
    (requirement.effectiveTo === null || requirement.effectiveTo >= asOf)
  );
}

function compareDesc(left: string | null, right: string | null): number {
  if (left === right) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  return right.localeCompare(left);
}

function compareRequirementPriority(
  left: CertGateRequirement,
  right: CertGateRequirement,
  tenantId: string | null,
): number {
  const leftScope = tenantId !== null && left.tenantId === tenantId ? 0 : 1;
  const rightScope = tenantId !== null && right.tenantId === tenantId ? 0 : 1;
  if (leftScope !== rightScope) return leftScope - rightScope;

  const effective = compareDesc(left.effectiveFrom, right.effectiveFrom);
  if (effective !== 0) return effective;
  const updated = compareDesc(left.updatedAt, right.updatedAt);
  if (updated !== 0) return updated;
  const created = compareDesc(left.createdAt, right.createdAt);
  if (created !== 0) return created;
  return compareDesc(left.id, right.id);
}

function isMapRegistry(
  registry: CertGateRegistry,
): registry is ReadonlyMap<string, CertGateRequirement> {
  return typeof (registry as { get?: unknown }).get === 'function';
}

export function resolveCertGateRegistry(
  requirements: readonly CertAllowanceRequirementInput[],
  options: { tenantId: string | null; asOfDate: string },
): Map<string, CertGateRequirement> {
  const asOf = requiredDateOnly(options.asOfDate, 'asOfDate');
  const tenantId = options.tenantId ?? null;
  const resolved = new Map<string, CertGateRequirement>();

  for (const rawRequirement of requirements) {
    const requirement = normaliseCertAllowanceRequirement(rawRequirement);
    const inScope = requirement.tenantId === null || requirement.tenantId === tenantId;
    if (!inScope || !isCertGateRequirementEffective(requirement, asOf)) continue;

    const key = certAllowanceKey(requirement.awardCode, requirement.allowanceCode);
    const current = resolved.get(key);
    if (!current || compareRequirementPriority(requirement, current, tenantId) < 0) {
      resolved.set(key, requirement);
    }
  }

  return resolved;
}

export function getCertGateRequirement(
  registry: CertGateRegistry,
  awardCode: string,
  allowanceCode: string,
): CertGateRequirement | undefined {
  const key = certAllowanceKey(awardCode, allowanceCode);
  return isMapRegistry(registry) ? registry.get(key) : registry[key];
}

export function isActiveCertificationCurrentForRequirement(
  certification: ActiveCertificationInput,
  requirement: CertGateRequirement,
  asOfDate: string,
): boolean {
  const asOf = requiredDateOnly(asOfDate, 'asOfDate');
  const certType = upperToken(certification.certificationType ?? certification.certification_type);
  const reqCode = upperToken(certification.requirementCode ?? certification.requirement_code);
  const status = upperToken(certification.status);
  const verificationStatus = upperToken(
    certification.verificationStatus ?? certification.verification_status,
  );
  const expiresAt = dateOnly(certification.expiresAt ?? certification.expires_at, 'expiresAt');

  return (
    certType === requirement.certificationType &&
    (reqCode === requirement.requirementCode || certType === requirement.requirementCode) &&
    status === 'CURRENT' &&
    verificationStatus === 'VERIFIED' &&
    expiresAt !== null &&
    expiresAt >= asOf
  );
}

export function isCertGateSatisfied(
  requirement: CertGateRequirement,
  activeCertifications: readonly ActiveCertificationInput[],
  asOfDate: string,
): boolean {
  if (!requirement.required) return true;
  return activeCertifications.some((certification) =>
    isActiveCertificationCurrentForRequirement(certification, requirement, asOfDate),
  );
}

export function hasRequiredCertificationForAllowance(input: {
  registry: CertGateRegistry;
  awardCode: string;
  allowanceCode: string;
  activeCertifications: readonly ActiveCertificationInput[];
  asOfDate: string;
}): boolean {
  const requirement = getCertGateRequirement(input.registry, input.awardCode, input.allowanceCode);
  if (!requirement) return true;
  return isCertGateSatisfied(requirement, input.activeCertifications, input.asOfDate);
}

export function findMissingCertGateRequirements(input: {
  registry: CertGateRegistry;
  activeCertifications: readonly ActiveCertificationInput[];
  asOfDate: string;
}): CertGateRequirement[] {
  const requirements = isMapRegistry(input.registry)
    ? [...input.registry.values()]
    : Object.values(input.registry);
  return requirements.filter(
    (requirement) =>
      requirement.required &&
      !isCertGateSatisfied(requirement, input.activeCertifications, input.asOfDate),
  );
}
